import { useState } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Switch,
  Button,
  Group,
  Divider,
} from "@mantine/core";
import { TemplateSectionEditor } from "./TemplateSectionEditor";
import { TemplateSection } from "../../../types/reportTemplate";
import {
  useCreateReportTemplate,
  useUpdateReportTemplate,
} from "../../../hooks/admin/useReportTemplates";
import { ReportTemplate } from "../../../services/admin/reporttemplates.service";

interface ReportTemplateFormProps {
  template?: ReportTemplate | null;
  onClose: () => void;
}

export function ReportTemplateForm({
  template,
  onClose,
}: ReportTemplateFormProps) {
  const isEdit = !!template;

  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [isDefault, setIsDefault] = useState(template?.isDefault || false);
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [title, setTitle] = useState(template?.templateContent?.title || "");
  const [sections, setSections] = useState<TemplateSection[]>(
    template?.templateContent?.sections || []
  );

  const createMutation = useCreateReportTemplate();
  const updateMutation = useUpdateReportTemplate();

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    if (!name.trim()) return;

    const templateContent =
      sections.length > 0
        ? {
            version: "1.0" as const,
            title: title || name,
            sections,
          }
        : undefined;

    if (isEdit && template) {
      updateMutation.mutate(
        {
          id: template.id,
          name,
          description,
          templateContent,
          isDefault,
          isActive,
        },
        { onSuccess: onClose }
      );
    } else {
      createMutation.mutate(
        { name, description, templateContent, isDefault, isActive },
        { onSuccess: onClose }
      );
    }
  };

  return (
    <Stack gap="md">
      <TextInput
        label="Template Name"
        placeholder="e.g. Surgical Report"
        required
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <Textarea
        label="Description"
        placeholder="Brief description of this template"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        rows={2}
      />

      <TextInput
        label="Document Title"
        placeholder="e.g. PATHOLOGY REPORT"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
      />

      <Group>
        <Switch
          label="Set as default template"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.currentTarget.checked)}
        />
        <Switch
          label="Active"
          checked={isActive}
          onChange={(e) => setIsActive(e.currentTarget.checked)}
        />
      </Group>

      <Divider />

      <TemplateSectionEditor sections={sections} onChange={setSections} />

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isPending}
          disabled={!name.trim()}
        >
          {isEdit ? "Update Template" : "Create Template"}
        </Button>
      </Group>
    </Stack>
  );
}
